import { makeAutoObservable } from "mobx";
import Menu, {
    AdditionalSellUnit,
    Filter, HalfAndHalfNavigation,
    MenuItem,
    MenuItemCombination,
    MenuNavigation,
    Recommendation,
    ScanArticleResult
} from "../@types/stores/menu";
import { hideLoader, showLoader } from "../helpers/ComponentHelper";
import { moveToSubCategory } from "../helpers/DomHelper";
import openApi from "../api/open";
import config from "../config/default.json";
import orderStore from "./order";
import { OrderType } from "../@types/enums/OrderType";
import apiOpen from "../api/open";
import {isMenuItem, isMenuItemCombination} from "../helpers/ApiHelper";
import lodash from "lodash";
import {OrderLine} from "../@types/stores/order";

const getMenuReTryDelay = config.menu.getMenuReTryDelay;

class MenuStore {
    readonly emptyFilter: Filter = {
        allergens: [],
        preferences: [],
    };

    readonly emptyNavigation: MenuNavigation = {
        category: 0,
        subCategory: 0,
        isAddedToOrder: false
    };

    readonly emptyHalfAndHalfNavigation: HalfAndHalfNavigation = {
        category: 0,
        subCategory: 0
    };

    getMenuInProgress = false;
    menu: Menu[] | null = null;
    recommendations: Recommendation[] | null = null;
    navigation: MenuNavigation = lodash.cloneDeep(this.emptyNavigation);
    filter: Filter = lodash.cloneDeep(this.emptyFilter);
    _returnToScanArticle = false;

    halfAndHalfNavigation: HalfAndHalfNavigation = lodash.cloneDeep(this.emptyHalfAndHalfNavigation);
    _selectedHalfAndHalfItems: (OrderLine | null)[] = [];
    _selectedHalfAndHalfStep: number = 0;
    _editHalfAndHalfId: string = "";
    _selectedAdditionalSellUnitHalfAndHalf: AdditionalSellUnit | null = null;


    constructor() {
        makeAutoObservable(this);
    }

    public get selectedHalfAndHalfItems(): (OrderLine | null)[] {
        return this._selectedHalfAndHalfItems;
    }

    public set selectedHalfAndHalfItems(value: (OrderLine | null)[]) {
        this._selectedHalfAndHalfItems = value;
    }

    public get selectedHalfAndHalfStep(): number {
        return this._selectedHalfAndHalfStep;
    }

    public set selectedHalfAndHalfStep(value: number) {
        this._selectedHalfAndHalfStep = value;
    }

    get returnToScanArticle() {
        return this._returnToScanArticle;
    }

    set returnToScanArticle(value) {
        this._returnToScanArticle = value;
    }

    get editHalfAndHalfId(): string {
        return this._editHalfAndHalfId;
    }

    set editHalfAndHalfId(value: string) {
        this._editHalfAndHalfId = value;
    }

    get selectedAdditionalSellUnitHalfAndHalf(): AdditionalSellUnit | null {
        return this._selectedAdditionalSellUnitHalfAndHalf;
    }

    set selectedAdditionalSellUnitHalfAndHalf(value: AdditionalSellUnit | null) {
        this._selectedAdditionalSellUnitHalfAndHalf = value;
    }






    addItemToOrder() {
        this.navigation.isAddedToOrder = true;
    }

    resetAddItemToOrder() {
        this.navigation.isAddedToOrder = false;
    }

    resetFilter() {
        this.filter = lodash.cloneDeep(this.emptyFilter);
    }

    async getMenu(force: boolean = false): Promise<void> {
        showLoader();
        this.filter = lodash.cloneDeep(this.emptyFilter);

        if (this.getMenuInProgress) {
            setTimeout(async () => {
                await this.getMenu()
            }, getMenuReTryDelay);
            return;
        }

        if (force || !this.menu) {
            const refresh = this.menu !== null;
            this.getMenuInProgress = true;
            const menuType = orderStore.order.type;

            let _menu: Menu[] | null = null;
            let _recommendations: Recommendation[] | null = null;
            if (menuType === OrderType.EAT_IN) {
                _menu = await openApi.getEatInMenu(refresh);
                _recommendations = await openApi.getEatInRecommendations(!this.recommendations);
            }
            if (menuType === OrderType.TAKEAWAY) {
                _menu = await openApi.getTakeAwayMenu(refresh);
                _recommendations = await openApi.getTakeAwayRecommendations(!this.recommendations);
            }

            if (_menu)
                this.menu = _menu;
            if (_recommendations)
                this.recommendations = _recommendations;

            await apiOpen.getFilters();

            this.getMenuInProgress = false;
        }

        if (!this.navigation.category && !this.navigation.subCategory)
            this.changeCategory(this.menu?.at(0)?.id || 0);

        hideLoader();
    }

    changeCategory(id: number) {
        this.navigation.category = id;

        const category = this.menu?.find(x => x.id === id);

        this.changeSubCategory(category?.submenu?.at(0)?.id || 0);
    }

    changeSubCategory(id: number, goTo?: boolean) {
        this.navigation.subCategory = id;

        if (goTo)
            moveToSubCategory(id);
    }

    changeMenuItem(id: number | undefined = undefined) {
        this.navigation.item = id;
    }

    changeRecommendationItem(id: number | undefined = undefined) {
        this.navigation.recommendationItem = id;
    }

    changeRecommendationGroup(id: number | undefined = undefined) {
        this.navigation.recommendationGroupId = id;
    }

    get activeCategory(): number {
        return this.navigation.category;
    }

    get activeSubCategory(): number {
        return this.navigation.subCategory;
    }

    getItemByNumber(number: string, categoryId: number = this.navigation.category, subCategoryId: number = this.navigation.subCategory): MenuItem | undefined {
        if (categoryId && subCategoryId) {
            const category = this.menu?.find(x => x.id === categoryId);
            if (category) {
                const subCategory = category?.submenu?.find(x => x.id === subCategoryId);
                if (subCategory) {
                    return subCategory.articles?.find(x => {
                        if (isMenuItem(x)) {
                            if (isMenuItem(x)) {
                                x = x as MenuItem;
                                return x.number === number;
                            }
                        }
                        return false;
                    }) as MenuItem | undefined;
                }
            }
        }
        return;
    }

    getItemById(id: number, categoryId: number = this.navigation.category, subCategoryId: number = this.navigation.subCategory): MenuItem | undefined {
        if (categoryId && subCategoryId) {
            const category = this.menu?.find(x => x.id === categoryId);
            if (category) {
                const subCategory = category?.submenu?.find(x => x.id === subCategoryId);
                if (subCategory) {
                    return subCategory.articles?.find(x => {
                        if (isMenuItem(x)) {
                            x = x as MenuItem;
                            return x.id === id;
                        }
                        return false;
                    }) as MenuItem | undefined;
                }
            }
        }
        return;
    }

    getCombinationById(id: number): MenuItemCombination | undefined {
        if (this.navigation.category && this.navigation.subCategory) {
            const category = this.menu?.find(x => x.id === this.navigation.category);
            if (category) {
                const subCategory = category?.submenu?.find(x => x.id === this.navigation.subCategory);
                if (subCategory) {
                    return subCategory.articles?.find(x => {
                        if (isMenuItemCombination(x)) {
                            x = x as MenuItem;
                            return x.id === id;
                        }
                        return false;
                    }) as MenuItemCombination | undefined;
                }
            }
        }
        return;
    }

    getItem(): MenuItem | undefined {
        if (this.navigation.category && this.navigation.subCategory && this.navigation.item) {
            const category = this.menu?.find(x => x.id === this.navigation.category);
            if (category?.submenu) {
                for (const subCategory of category.submenu) {
                    let item = subCategory.articles?.find(x => x.id === this.navigation.item);
                    if (item && isMenuItem(item)) {
                        item = item as MenuItem;
                        return item;
                    }
                }
            }
        }
        return;
    }

    getItemByIds(categoryId: number, subCategoryId: number, itemNumber: string): MenuItem | undefined {
        const category = this.menu?.find(x => x.id === categoryId);
        if (category) {
            const subCategory = category?.submenu?.find(x => x.id === subCategoryId);
            if (subCategory) {
                let item: MenuItem | undefined;
                subCategory.articles?.every(x => {
                    if (isMenuItem(x)) {
                        x = x as MenuItem;
                        if (x.number === itemNumber) {
                            item = x;
                            return false;
                        }
                        return true;
                    }
                    else if (isMenuItemCombination(x)) {
                        x = x as MenuItemCombination;
                        x.combination.every(step => {
                            const article = step.articles.find(x => x.article.number === itemNumber);
                            if (article) {
                                item = article.article;
                                return false;
                            }
                            return true;
                        });
                        if (item) return true;
                        return true;
                    }
                    return false;
                });

                return item;
            }
        }
        return;
    }

    getRecommendationItem(): MenuItem | undefined {
        if (this.navigation.recommendationGroupId && this.navigation.recommendationItem) {
            const recommendationGroup = this.recommendations?.find(x => x.id === this.navigation.recommendationGroupId);
            if (recommendationGroup) {
                return recommendationGroup.articles?.find(x => x.id === this.navigation.recommendationItem);
            }
        }
        return;
    }

    getRecommendationItemByNumber(groupId: number, number: string): MenuItem | undefined {
        const group = this.recommendations?.find(x => x.id === groupId);
        if (group) {
            return group.articles.find(x => x.number === number);
        }

        return;
    }

    resetNavigation() {
        this.navigation = lodash.cloneDeep(this.emptyNavigation);
    }

    addCombinationRecommendationGroupIds(ids: number[]): void {
        if (!Array.isArray(this.navigation.combinationRecommendationGroupIds))
            this.navigation.combinationRecommendationGroupIds = [];

        ids.forEach((id) => {
           if (this.navigation.combinationRecommendationGroupIds?.indexOf(id) === -1)
               this.navigation.combinationRecommendationGroupIds.push(id);
        });
    }

    findArticle(str: string): ScanArticleResult[] {
        if (!this.menu) return [];

        const result: ScanArticleResult[] = [];

        for (const category of this.menu) {
            if (category.submenu) {
                for (const subCategory of category.submenu) {

                    //by barcode
                    let item = subCategory.articles?.find(x => {
                        if (isMenuItem(x)) {
                            return (x as MenuItem).barcodes?.includes(str) ? x : undefined;
                        }
                        else {
                            return (x as MenuItemCombination).barcodes?.includes(str) ? x : undefined;
                        }
                    });

                    if (item)
                        return [{
                            categoryId: category.id,
                            subCategoryId: subCategory.id,
                            item
                        }];

                    // by id
                    item = subCategory.articles?.find(x => {
                        if (isMenuItem(x)) {
                            return (x as MenuItem).number === str ? x : undefined;
                        }
                        else {
                            return (x as MenuItemCombination).id === parseInt(str, 10) ? x : undefined;
                        }
                    });

                    if (item)
                        return [{
                            categoryId: category.id,
                            subCategoryId: subCategory.id,
                            item
                        }];

                    // by name
                    const items = subCategory.articles?.filter(x => {
                        return x.name.toLowerCase().includes(str.toLowerCase());
                    }) || [];

                    items.forEach(item => {
                        if (result.some(x => x.item.id === item.id))
                            return;

                        result.push({
                            categoryId: category.id,
                            subCategoryId: subCategory.id,
                            item
                        });
                    });
                }
            }
        }

        return result;
    }

    resetHalfAndHalfNavigation() {
        this.halfAndHalfNavigation = lodash.cloneDeep(this.emptyHalfAndHalfNavigation);
        this._selectedAdditionalSellUnitHalfAndHalf = null;
        this._selectedHalfAndHalfItems = [];
        this._selectedHalfAndHalfStep = 0;
    }
}

const menuStore = new MenuStore();
export default menuStore;
